/**
 * Speech (brief section 8, decision of Oct 28, 2026): the browser's own
 * speech recognition and voice at launch, behind this interface, so Azure
 * AI Speech can replace it later without changing the screens. English.
 * Where the browser cannot listen, the screens work by typing.
 */
export interface Speech {
  /** The browser can listen (Chrome, Edge, Safari); false: typing only. */
  readonly canListen: boolean;
  /**
   * Listens until the person stops talking (one answer), or until stop()
   * for a dictation (continuous). Partial words come through onWords.
   */
  listen(options?: { continuous?: boolean; onWords?(text: string): void }): Promise<string>;
  stop(): void;
  /** Says it, in the most natural voice of the device. */
  speak(text: string): Promise<void>;
  /** Stops talking at once (the person answers). */
  silence(): void;
}

interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult:
    | ((e: {
        resultIndex: number;
        results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
      }) => void)
    | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}
type RecognitionCtor = new () => Recognition;

/**
 * The words of a recognition so far, from all its results (final ones,
 * then the one still being heard). Chrome on Android repeats in each
 * result what the previous ones said ("I met", "I met Giulia", ...): a
 * result that starts with what is already there replaces it, so a
 * dictation never comes back doubled (round 2, Qualify by voice).
 */
export function joinResults(
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>,
): { finals: string; partial: string } {
  const norm = (t: string) => t.trim().toLowerCase().replace(/\s+/g, ' ');
  let finals = '';
  let partial = '';
  for (let i = 0; i < results.length; i++) {
    const result = results[i]!;
    const text = result[0]?.transcript.trim() ?? '';
    if (!text) continue;
    if (!result.isFinal) {
      partial = text;
      continue;
    }
    const before = norm(finals);
    if (before && norm(text).startsWith(before)) finals = text;
    else if (before.endsWith(norm(text))) continue;
    else finals = finals ? `${finals} ${text}` : text;
  }
  // A partial that repeats the finals (Android) only adds what is new.
  if (partial && finals && norm(partial).startsWith(norm(finals))) {
    partial = partial.slice(finals.length).trim();
  }
  return { finals, partial };
}

function recognitionCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** The most natural English voice of the device (prototype: voiceScore). */
export function voiceScore(v: { name: string; lang: string; localService?: boolean }): number {
  const n = v.name;
  if (!/^en/i.test(v.lang)) return -1;
  if (/Natural|Neural/i.test(n)) {
    return (
      100 + (/en-US/i.test(v.lang) ? 5 : 0) + (/Aria|Jenny|Ava|Emma|Michelle/i.test(n) ? 3 : 0)
    );
  }
  if (/Google US English/i.test(n)) return 90;
  if (/Google UK English Female/i.test(n)) return 85;
  if (/Ava|Samantha|Allison|Susan|Serena|Karen|Moira/i.test(n)) {
    return 80 + (/Premium|Enhanced/i.test(n) ? 8 : 0);
  }
  if (/Google UK English Male/i.test(n)) return 70;
  if (/Google/i.test(n)) return 60;
  return v.localService ? 20 : 30;
}

export function browserSpeech(): Speech {
  const Ctor = recognitionCtor();
  let current: Recognition | null = null;
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined;
  const best = () =>
    [...(synth?.getVoices() ?? [])].sort((a, b) => voiceScore(b) - voiceScore(a))[0] ?? null;
  return {
    canListen: Boolean(Ctor),
    listen({ continuous = false, onWords } = {}) {
      return new Promise((resolve, reject) => {
        if (!Ctor) return reject(new Error('no-recognition'));
        current?.stop();
        const r = new Ctor();
        current = r;
        r.lang = 'en-US';
        r.continuous = continuous;
        r.interimResults = true;
        let finals = '';
        let partial = '';
        r.onresult = (e) => {
          // Read again from the first result each time: never doubled.
          ({ finals, partial } = joinResults(e.results));
          onWords?.(`${finals} ${partial}`.trim());
        };
        r.onerror = (e) => {
          if (e.error === 'no-speech' || e.error === 'aborted') return;
          reject(new Error(e.error));
        };
        r.onend = () => {
          if (current === r) current = null;
          resolve(`${finals} ${partial}`.trim());
        };
        r.start();
      });
    },
    stop() {
      current?.stop();
    },
    speak(text) {
      return new Promise((resolve) => {
        if (!synth || typeof SpeechSynthesisUtterance === 'undefined') return resolve();
        synth.cancel();
        const u = new SpeechSynthesisUtterance(text);
        const v = best();
        if (v) {
          u.voice = v;
          u.lang = v.lang;
        } else u.lang = 'en-US';
        u.rate = /Google/i.test(v?.name ?? '') ? 1 : 0.97;
        // Some browsers never say they finished (Chrome, long sentences, no
        // audio device): the conversation never waits longer than the words take.
        const words = text.split(/\s+/).length;
        const timer = setTimeout(resolve, 1500 + words * 450);
        const done = () => {
          clearTimeout(timer);
          resolve();
        };
        u.onend = done;
        u.onerror = done;
        synth.speak(u);
      });
    },
    silence() {
      synth?.cancel();
    },
  };
}

/** The speech of this device (one place to switch to another service later). */
export const createSpeech = (): Speech => browserSpeech();
